<template>
  <FullPage class="labels-screen">
    <div class="labels-header">
      <BaseNumberedHeader title="Labels" :number="labelsCount" />
      <BaseButton kind="main" type="primary" text="New" @click="createLabel">
        <template #icon>
          <PlusIcon />
        </template>
      </BaseButton>
    </div>

    <div v-if="isLoading" class="loader">
      <BaseSkeletonList is-compact :size="10" />
    </div>

    <div v-else class="label-list">
      <div v-for="label in labels" :key="label.id" class="label-row">
        <div class="label-link" @click="navigateToLabel(label.id, $event)">
          <RTag
            class="label-tag"
            :text="label.title"
            :theme="label.theme"
            :has-padding="false"
            skin="dot"
          />
        </div>
        <div class="label-counts">
          <span class="count-tag">
            <TaskIcon class="count-icon" size="s" />
            <span class="count-value">{{ label.taskCount }}</span>
          </span>
          <span class="count-tag">
            <EventIcon class="count-icon" />
            <span class="count-value">{{ label.eventCount }}</span>
          </span>
          <span class="count-tag">
            <PageIcon class="count-icon" />
            <span class="count-value">{{ label.pageCount }}</span>
          </span>
          <span class="count-tag">
            <PersonIcon class="count-icon" />
            <span class="count-value">{{ label.peopleCount }}</span>
          </span>
        </div>
      </div>
    </div>
  </FullPage>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";

import type { GraphDatabase } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseNumberedHeader from "@/components/base/BaseNumberedHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import { resolveLabelColor } from "@/components/color";
import RTag from "@/components/elements/RTag.vue";
import {
  buildLabelCountsQuery,
  getGraphQuery,
  runCompiledQuery,
} from "@/components/graph/query";
import { asArray, asCountNumber } from "@/components/graph/result";
import { displayTitle } from "@/components/graph/title";
import EventIcon from "@/components/icons/EventIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { getLabels, labelsFromGraphResult } from "@/components/labels";
import { createObject } from "@/components/modals/object";
import { LABELS_TABLE_ID } from "@/components/tables";
import FullPage from "@/components/templates/FullPage.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo, navigateToId } from "@/utils/navigate";

type LabelRow = {
  id: string;
  title: string;
  theme: string;
  taskCount: number;
  eventCount: number;
  pageCount: number;
  peopleCount: number;
};

export default defineComponent({
  name: "LabelsScreen",
  components: {
    BaseButton,
    BaseNumberedHeader,
    BaseSkeletonList,
    EventIcon,
    FullPage,
    PageIcon,
    PersonIcon,
    PlusIcon,
    RTag,
    TaskIcon,
  },
  props: {
    // Organization scope, set by the org-scoped route. Labels are
    // personal-workspace only today; reserved for per-org labels support.
    orgId: { type: String, required: false },
  },
  async setup() {
    const controller = inject.controller();
    const router = inject.router();
    const profile = inject.profile();
    const workspaceDatabases = inject.workspaceDatabases();
    const baseLogger = inject.logger();
    const logger = baseLogger.forModule("Labels");
    const browserStore = useBrowserStore(profile.value.user.accountId);

    const workspaceId = computed(() => profile.value.user.personal.workspaceId);

    const releaser = useReleaser();

    const databaseById = (id: string): GraphDatabase | null => {
      const ws = workspaceDatabases.value?.[workspaceId.value];
      if (!ws) return null;
      return ws[id] ?? null;
    };

    const labelsDatabase = computed(() => databaseById(LABELS_TABLE_ID));
    const labelsWorkspaceId = computed(() =>
      labelsDatabase.value ? workspaceId.value : null
    );

    const queryResult = await releaser.add(
      bindAsync(labelsWorkspaceId, async (id) => {
        if (!id) {
          if (workspaceDatabases.value) {
            logger.error(
              `Unable to find labels database with ID "${LABELS_TABLE_ID}" in your current workspace.`
            );
          }
          return releasableRef(undefined);
        }
        return getLabels(id, controller);
      })
    );

    const isLoading = computed(
      () => labelsDatabase.value != null && queryResult.value === undefined
    );

    const labelsBase = computed<
      Array<Pick<LabelRow, "id" | "title" | "theme">>
    >(() =>
      labelsFromGraphResult(queryResult.value, displayTitle()).map(
        ({ id, title }) => ({
          id,
          title,
          theme: `calendar-${resolveLabelColor(browserStore, id, title)}`,
        })
      )
    );

    // All counts come from a single batched query (one compile+run instead
    // of one per label × type); the result is positional, in the order of
    // `relationTypeNames` and `labelsBase`.
    const relationTypeNames = ["tasks", "events", "pages", "people"];

    // A string source, so recomputes that keep the same label ids (e.g. a
    // label rename refreshing the list) don't recompile the query.
    const countsQueryString = computed(() =>
      labelsBase.value.length === 0
        ? null
        : buildLabelCountsQuery(
            relationTypeNames,
            labelsBase.value.map((l) => l.id)
          )
    );

    const countsResult = await releaser.add(
      bindAsync(countsQueryString, async (queryString) => {
        if (!queryString) return releasableRef(undefined);
        logger.debug("Compiling label counts query", { queryString });
        const query = await getGraphQuery(
          workspaceId.value,
          queryString,
          controller
        );
        if (!query) {
          logger.error("Unable to compile the label counts query", {
            queryString,
          });
          return releasableRef(undefined);
        }
        return await runCompiledQuery(query, controller);
      })
    );

    const countFor = (labelIndex: number, typeIndex: number): number => {
      const rows = asArray(countsResult.value ?? null);
      const row = rows?.[labelIndex];
      const cells = row === undefined ? undefined : asArray(row);
      const cell = cells?.[typeIndex];
      return cell === undefined ? 0 : (asCountNumber(cell) ?? 0);
    };

    const labels = computed<LabelRow[]>(() =>
      labelsBase.value.map((l, labelIndex) => ({
        ...l,
        taskCount: countFor(labelIndex, 0),
        eventCount: countFor(labelIndex, 1),
        pageCount: countFor(labelIndex, 2),
        peopleCount: countFor(labelIndex, 3),
      }))
    );

    const labelsCount = computed(() => labels.value.length);

    const createLabel = async () => {
      const wsId = workspaceId.value;
      const modal = createObject(baseLogger);
      try {
        const result = await modal.openObjectModal(
          LABELS_TABLE_ID,
          wsId,
          controller,
          profile
        );
        if (result?.shouldRedirect && result.objectId) {
          navigateToId(
            router,
            controller,
            {},
            wsId,
            LABELS_TABLE_ID,
            result.objectId
          );
        }
      } finally {
        modal.release();
      }
    };

    const navigateToLabel = (
      labelId: string,
      event?: MouseEvent | KeyboardEvent
    ) => {
      navigateTo(
        RouteNames.Label,
        router,
        {
          params: { labelId },
        },
        event
      );
    };

    return {
      createLabel,
      isLoading,
      labels,
      labelsCount,
      navigateToLabel,
    };
  },
});
</script>

<style scoped lang="scss">
.labels-screen {
  .labels-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .label-list {
    display: flex;
    flex-direction: column;
  }

  .label-row {
    display: flex;
    align-items: center;
    column-gap: 24px;
    padding: 6px 0;
  }

  .label-link {
    cursor: pointer;

    &:hover :deep(.box) {
      @include themed() {
        background-color: t($grey-05);
      }
    }
  }

  .label-tag {
    max-width: 200px;
    width: fit-content;
  }

  .label-counts {
    display: flex;
    align-items: center;
    column-gap: 12px;
  }

  .count-tag {
    display: flex;
    align-items: center;
    column-gap: 4px;

    .count-icon {
      width: 14px;
      height: 14px;

      :deep(.stroke) {
        @include themed() {
          stroke: t($grey-40);
        }
      }

      :deep(.fill) {
        @include themed() {
          fill: t($grey-40);
        }
      }
    }

    .count-value {
      @extend %font-caption-regular;

      @include themed() {
        color: t($grey-50);
      }
    }
  }

  .loader {
    padding-top: 8px;
  }

  .error {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
